/**
 * Product Quick View Modal
 * Handles opening a popup with product details, variant selection, and add to cart/wishlist
 */

class ProductQuickView {
    constructor() {
        this.modal = document.getElementById("product-quick-view-modal");
        this.content = document.getElementById("quick-view-content");
        this.currentProduct = null;
        this.selectedVariant = null;
        /** @type {Map<string, boolean>} */
        this.shippingMap = new Map();
        this.currentImageIndex = 0;

        this.init();
    }

    init() {
        // Close modal listeners
        document.querySelectorAll("[data-quick-view-close]").forEach((btn) => {
            btn.addEventListener("click", () => this.close());
        });

        // Close on escape key
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && this.modal.getAttribute("aria-hidden") === "false") {
                this.close();
            }
        });

        // Attach to wishlist buttons
        this.attachWishlistButtons();
    }

    attachWishlistButtons() {
        document.querySelectorAll("[data-wishlist-button]").forEach((button) => {
            // Remove existing click handlers and add new one
            const newButton = button.cloneNode(true);
            button.parentNode.replaceChild(newButton, button);

            newButton.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                const productHandle = newButton.dataset.productHandle;
                this.open(productHandle);
            });
        });
    }

    async open(productHandle) {
        if (!productHandle) return;

        // Show modal with loading state
        this.modal.setAttribute("aria-hidden", "false");
        document.body.style.overflow = "hidden";

        // Show loading spinner
        this.content.innerHTML = `
      <div class="quick-view-loading">
        <div class="quick-view-spinner"></div>
      </div>
    `;

        try {
            // Fetch product data
            const response = await fetch(`/products/${productHandle}.js`);
            const product = await response.json();
            this.currentProduct = product;
            this.selectedVariant = product.variants[0];
            this.currentImageIndex = 0;

            // Fetch HTML to parse variant shipping flags (data-shipping-speed) rendered by Liquid on the product page
            this.shippingMap = await this.#fetchVariantShippingMap(productHandle);

            // Render product
            this.renderProduct(product);
        } catch (error) {
            console.error("Failed to load product:", error);
            this.content.innerHTML = `
        <div style="text-align: center; padding: 40px;">
          <p>Failed to load product. Please try again.</p>
        </div>
      `;
        }
    }

    close() {
        this.modal.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
        this.currentProduct = null;
        this.selectedVariant = null;
    }

    renderProduct(product) {
        const variant = this.selectedVariant;
        const price = this.formatMoney(variant.price);
        const comparePrice = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : null;
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

        // Get vendor/brand (hidden per spec)
        const vendor = "";

        // Prepare images for gallery
        const images = this.#getProductImages(product);
        // Try to start from variant's featured image if present
        if (variant?.featured_image?.src) {
            const idx = images.findIndex(
                (src) => this.#normalizeUrl(src) === this.#normalizeUrl(variant.featured_image.src),
            );
            this.currentImageIndex = idx >= 0 ? idx : this.currentImageIndex;
        }

        // Build variant options HTML
        let variantsHTML = "";
        if (product.variants.length > 1) {
            const optionNames = product.options.map((opt) => opt.name || opt);
            optionNames.forEach((optionName, optionIndex) => {
                const values = [...new Set(product.variants.map((v) => v.options[optionIndex]))];
                const isSizeOption = optionName.toLowerCase().includes("size");
                const isColorOption =
                    optionName.toLowerCase().includes("color") || optionName.toLowerCase().includes("colour");

                const optionsHTML = values
                    .map((value) => {
                        const matchingVariant = product.variants.find(
                            (v) =>
                                v.options[optionIndex] === value &&
                                v.options.every((opt, i) =>
                                    i === optionIndex ? true : opt === this.selectedVariant.options[i],
                                ),
                        );
                        const available = matchingVariant ? matchingVariant.available : false;
                        const isSelected = matchingVariant ? matchingVariant.id === this.selectedVariant.id : false;
                        const valuePrice = matchingVariant ? this.formatMoney(matchingVariant.price) : "";
                        const variantSpeed = matchingVariant
                            ? this.shippingMap.get(String(matchingVariant.id)) || "standard"
                            : "standard";
                        const isFastOrExpress = variantSpeed === "express" || variantSpeed === "fast";
                        const truckTpl = document.getElementById("wishlist-truck-icon")?.innerHTML;
                        const shipIcon =
                            isSizeOption && isFastOrExpress
                                ? `<span class="qv-ship-tag" aria-hidden="true">${
                                      truckTpl ||
                                      '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M3 6h10a1 1 0 0 1 1 1v5h2.586a1 1 0 0 1 .707.293l2.828 2.828A1 1 0 0 1 21 16v2a1 1 0 0 1-1 1h-1a2 2 0 1 1-4 0H9a2 2 0 1 1-4 0H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Zm11 6V8H4v9h.17A2.999 2.999 0 0 1 9 18h6a3 3 0 0 1 4.83 0H20v-1.586L17.586 12H14Z"/></svg></span>'
                                  }</span>`
                                : "";

                        return `
                                                    <button type="button" class="quick-view-variant-option qv-size-btn ${
                                                        isSelected ? "selected" : ""
                                                    } ${
                                                        available ? "" : "soldout"
                                                    }" data-option-value="${value}" data-available="${available}">
                                                        <span class="quick-view-variant-option__label">${value}</span>
                                                        ${
                                                            isSizeOption
                                                                ? `<span class=\"quick-view-variant-option__price\">${valuePrice}</span>`
                                                                : ""
                                                        }
                                                        ${shipIcon}
                                                    </button>
                                                `;
                    })
                    .join("");

                // Determine label text based on option type
                const modal = document.getElementById("product-quick-view-modal");
                const otherColorwaysText = modal?.dataset.translationOtherColorways || "Other colorways";
                const chooseSizeText = modal?.dataset.translationChooseSize || "Choose Size";
                const sizeGuideText = modal?.dataset.translationSizeGuide || "Size guide";

                let labelText = optionName;
                if (isColorOption) {
                    labelText = otherColorwaysText;
                } else if (isSizeOption) {
                    labelText = chooseSizeText;
                }

                // Size guide button for size options
                const sizeGuideHTML = isSizeOption
                    ? `
                    <button type="button" class="quick-view-size-guide">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="14" height="14" fill="currentColor">
                            <path d="M80 448C53.5 448 32 426.5 32 400L32 240C32 213.5 53.5 192 80 192L104 192L104 296C104 309.3 114.7 320 128 320C141.3 320 152 309.3 152 296L152 192L200 192L200 264C200 277.3 210.7 288 224 288C237.3 288 248 277.3 248 264L248 192L296 192L296 296C296 309.3 306.7 320 320 320C333.3 320 344 309.3 344 296L344 192L392 192L392 264C392 277.3 402.7 288 416 288C429.3 288 440 277.3 440 264L440 192L488 192L488 296C488 309.3 498.7 320 512 320C525.3 320 536 309.3 536 296L536 192L560 192C586.5 192 608 213.5 608 240L608 400C608 426.5 586.5 448 560 448L80 448z"/>
                        </svg>
                        ${sizeGuideText}
                    </button>
                `
                    : "";

                variantsHTML += `
                                    <div class="quick-view-variants">
                                        <label class="quick-view-variant-label">${labelText}${sizeGuideHTML}</label>
                                        <div class="quick-view-variant-options ${
                                            isSizeOption ? "qv-size-grid" : ""
                                        }" data-option-index="${optionIndex}">
                                            ${optionsHTML}
                                        </div>
                                    </div>
                                `;
            });
        }

        const selectedSpeed = this.shippingMap.get(String(this.selectedVariant.id)) || "standard";
        const truckNoteTpl = document.getElementById("wishlist-truck-icon")?.innerHTML;
        const fastDeliveryNoteTpl = document.getElementById("wishlist-fast-delivery-icon")?.innerHTML;
        const boxNoteTpl = document.getElementById("wishlist-box-icon")?.innerHTML;
        const fastDeliveryIconFallback =
            '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><path fill="currentColor" d="M177.6,184.7l-18.5-18.5l-5.4,5.4l21.2,21.2c1.5,1.5,3.9,1.5,5.4,0l40.8-40.8l-5.4-5.4L177.6,184.7z M32.4,213.2c-5.6,0-9.9-4.6-9.9-10.4V84.8h184.7v17.9h12.5V72.3h0c0-1.1-0.3-2.2-0.9-3.2l-30.6-38.9c-1.1-1.9-3.2-3-5.3-3h-30.6h-41.5H46.8c-2.2,0-4.2,1.2-5.3,3L10.9,69.1c-0.6,1-0.9,2.1-0.9,3.2h0v130.5c0,12.8,9.8,22.8,22.4,22.8h78.3h16.5v-12.5h-16.5H32.4z M119,39.6h33.2h27.1l26.5,32.7H119V39.6z M50.4,39.6h60.3v32.7H23.8L50.4,39.6z M187.4,111.6c-32.3,0-58.6,26.3-58.6,58.6c0,32.3,26.3,58.6,58.6,58.6s58.6-26.3,58.6-58.6C246,137.9,219.7,111.6,187.4,111.6z M187.4,220.5c-27.8,0-50.3-22.6-50.3-50.3s22.6-50.3,50.3-50.3s50.3,22.6,50.3,50.3S215.1,220.5,187.4,220.5z"/></svg></span>';
        const shippingNoteIcon =
            selectedSpeed === "express"
                ? truckNoteTpl ||
                  '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M3 6h10a1 1 0 0 1 1 1v5h2.586a1 1 0 0 1 .707.293l2.828 2.828A1 1 0 0 1 21 16v2a1 1 0 0 1-1 1h-1a2 2 0 1 1-4 0H9a2 2 0 1 1-4 0H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Zm11 6V8H4v9h.17A2.999 2.999 0 0 1 9 18h6a3 3 0 0 1 4.83 0H20v-1.586L17.586 12H14Z"/></svg></span>'
                : selectedSpeed === "fast"
                  ? fastDeliveryNoteTpl || fastDeliveryIconFallback
                  : boxNoteTpl ||
                    '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M21 8.5V18a2 2 0 0 1-1.106 1.789l-7 3.5a2 2 0 0 1-1.788 0l-7-3.5A2 2 0 0 1 3 18V8.5a2 2 0 0 1 1.106-1.789l7-3.5a2 2 0 0 1 1.788 0l7 3.5A2 2 0 0 1 21 8.5ZM12 4.118 5 7.618 12 11.118l7-3.5-7-3.5ZM5 9.382V18l7 3.5V12.882L5 9.382Zm9 12.118 5-2.5V9.382l-5 2.5V21.5Z"/></svg></span>';
        // Badge icon (truck) for fast shipping, fallback inline SVG if template missing
        const badgeTruckIcon =
            truckNoteTpl ||
            '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M3 6h10a1 1 0 0 1 1 1v5h2.586a1 1 0 0 1 .707.293l2.828 2.828A1 1 0 0 1 21 16v2a1 1 0 0 1-1 1h-1a2 2 0 1 1-4 0H9a2 2 0 1 1-4 0H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Zm11 6V8H4v9h.17A2.999 2.999 0 0 1 9 18h6a3 3 0 0 1 4.83 0H20v-1.586L17.586 12H14Z"/></svg></span>';
        const shippingNoteFast = Theme?.translations?.shipping_note_fast || "Shipped within 48h";
        const shippingNoteFastDelivery =
            Theme?.translations?.shipping_note_fast_delivery || "Fast Delivery (3-4 workdays)";
        const shippingNoteNormal = Theme?.translations?.shipping_note_normal || "Shipped in 2-10 business days";
        const shippingNoteText =
            selectedSpeed === "express"
                ? shippingNoteFast
                : selectedSpeed === "fast"
                  ? shippingNoteFastDelivery
                  : shippingNoteNormal;
        const shippingNote = `
                    <div class="qv-shipping-note">
                        <span class="qv-shipping-note__icon">${shippingNoteIcon}</span>
                        <span>${shippingNoteText}</span>
                    </div>`;

        this.content.innerHTML = `
                    <div class="quick-view-product">
                        <div class="quick-view-images">
                            ${
                                selectedSpeed === "express"
                                    ? `<div class="qv-badge qv-badge--ship-fast" aria-label="${shippingNoteFast}">${badgeTruckIcon}<span class="qv-badge__text">48H</span></div>`
                                    : selectedSpeed === "fast"
                                      ? `<div class="qv-badge qv-badge--ship-fast" aria-label="${shippingNoteFastDelivery}">${badgeTruckIcon}<span class="qv-badge__text">FAST</span></div>`
                                      : ""
                            }
                            <div class="qv-gallery" data-gallery data-count="${images.length}" data-index="${
                                this.currentImageIndex
                            }">
                                <div class="qv-gallery-track" style="transform: translateX(-${
                                    this.currentImageIndex * 100
                                }%);">
                                    ${images
                                        .map(
                                            (src) =>
                                                `<div class="qv-slide"><img src="${src}" alt="${product.title}" draggable="false" /></div>`,
                                        )
                                        .join("")}
                                </div>
                                ${
                                    images.length > 1
                                        ? `<button type="button" class="qv-gallery-nav qv-gallery-nav--prev" data-gallery-prev aria-label="Previous image">
                                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                                              </svg>
                                           </button>
                                           <button type="button" class="qv-gallery-nav qv-gallery-nav--next" data-gallery-next aria-label="Next image">
                                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                                                <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                                              </svg>
                                           </button>
                                           <div class="qv-gallery-progress"><div class="qv-gallery-progress__bar" style="width: ${
                                               100 / images.length
                                           }%; transform: translateX(${this.currentImageIndex * 100}%);"></div></div>`
                                        : ""
                                }
                            </div>
                        </div>
                        <div class="quick-view-info">
                            ${vendor ? `<div class=\"quick-view-brand\">${vendor}</div>` : ""}
                            <h2>${product.title}</h2>
                            <div class="quick-view-price">
                                ${onSale && comparePrice ? `<span class="compare-at-price">${comparePrice}</span>` : ""}
                                <span class="price ${onSale ? "price--sale" : ""}">${price}</span>
                            </div>
                            ${variantsHTML}
                            ${shippingNote}
                            <div class="quick-view-actions">
                                <button type="button" class="quick-view-btn quick-view-btn--cart" data-add-to-cart ${
                                    !variant.available ? "" : ""
                                }>Add to cart</button>
                                <button type="button" class="quick-view-btn quick-view-btn--notify" data-notify style="display:none;">Sold out, click to be notified</button>
                                <button type="button" class="quick-view-btn quick-view-btn--wishlist" data-add-to-wishlist>
                                    Add to wishlist
                                </button>
                            </div>
                        </div>
                    </div>
                `;

        // Attach event listeners
        this.attachVariantListeners();
        this.attachActionListeners();
        this.#initGalleryHandlers();

        // Toggle CTA based on availability
        this.#syncNotifyCTA();

        // Ensure styles for shipping tags are present
        this.#ensureStyles();
    }

    attachVariantListeners() {
        document.querySelectorAll(".quick-view-variant-option").forEach((button) => {
            button.addEventListener("click", () => {
                const wrapper = button.closest("[data-option-index]");
                const optionIndex = parseInt(wrapper?.dataset.optionIndex || "0", 10) || 0;
                const optionValue = button.dataset.optionValue;
                const available = button.getAttribute("data-available") !== "false";

                // Compute matching variant for this specific option change
                const newOptions = [...this.selectedVariant.options];
                newOptions[optionIndex] = optionValue;
                const matchingVariant = this.currentProduct.variants.find((v) =>
                    v.options.every((opt, i) => opt === newOptions[i]),
                );

                if (matchingVariant) {
                    this.selectedVariant = matchingVariant;
                    this.renderProduct(this.currentProduct);
                }
            });
        });
    }

    attachActionListeners() {
        // Add to cart
        const addToCartBtn = document.querySelector("[data-add-to-cart]");
        if (addToCartBtn) {
            addToCartBtn.addEventListener("click", () => this.addToCart());
        }

        // Notify
        const notifyBtn = document.querySelector("[data-notify]");
        if (notifyBtn) {
            notifyBtn.addEventListener("click", (e) => {
                e.preventDefault();
                const sizeIndex = (this.currentProduct?.options || []).findIndex((n) =>
                    String(n || "")
                        .toLowerCase()
                        .includes("size"),
                );
                const sizeName = sizeIndex >= 0 ? this.selectedVariant?.options?.[sizeIndex] || "" : "";
                const variantId = String(this.selectedVariant?.id || "");
                if (typeof window.openNotifyModal === "function") {
                    window.openNotifyModal(variantId, sizeName);
                }
            });
        }

        // Add to wishlist
        const addToWishlistBtn = document.querySelector("[data-add-to-wishlist]");
        if (addToWishlistBtn) {
            addToWishlistBtn.addEventListener("click", () => this.addToWishlist());
        }
    }

    #syncNotifyCTA() {
        try {
            const addBtn = document.querySelector("[data-add-to-cart]");
            const notifyBtn = document.querySelector("[data-notify]");
            if (!(addBtn && notifyBtn)) return;
            const isOOS = !(this.selectedVariant && this.selectedVariant.available);
            if (isOOS) {
                addBtn.style.display = "none";
                notifyBtn.style.removeProperty("display");
            } else {
                notifyBtn.style.display = "none";
                addBtn.style.removeProperty("display");
            }
        } catch (_) {}
    }

    async addToCart() {
        if (!this.selectedVariant || !this.selectedVariant.available) return;

        const btn = document.querySelector("[data-add-to-cart]");
        if (btn) {
            btn.disabled = true;
            btn.textContent = "Adding...";
        }

        try {
            // Ask for the cart sections in the same request, so the new cart
            // count can be read back without a second round trip.
            const sectionIds = [...document.querySelectorAll("cart-items-component")]
                .map((element) => element.dataset.sectionId)
                .filter(Boolean);

            const payload = {
                id: this.selectedVariant.id,
                quantity: 1,
            };

            if (sectionIds.length > 0) {
                payload.sections = sectionIds.join(",");
                payload.sections_url = window.location.pathname;
            }

            const response = await fetch("/cart/add.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                const cartData = await response.json();
                const sections = cartData.sections || {};
                const itemCount = await this.#resolveCartItemCount(sections, sectionIds);

                // Dispatch proper cart update event (ThemeEvents.cartUpdate = 'cart:update')
                document.dispatchEvent(
                    new CustomEvent("cart:update", {
                        bubbles: true,
                        detail: {
                            resource: cartData,
                            sourceId: "product-quick-view",
                            data: {
                                itemCount,
                                source: "product-quick-view",
                                variantId: this.selectedVariant.id,
                                productId: this.currentProduct.id,
                                // The upsell popup needs the handle to fetch
                                // the add-ons for a product whose page is not
                                // loaded. See assets/upsell-popup.js.
                                handle: this.currentProduct.handle,
                                sections,
                            },
                        },
                    }),
                );

                // Show success feedback
                if (btn) {
                    btn.textContent = "✓ Added!";
                }

                setTimeout(() => {
                    this.close();
                }, 500);
            } else {
                throw new Error("Failed to add to cart");
            }
        } catch (error) {
            console.error("Failed to add to cart:", error);
            if (btn) {
                btn.textContent = "Add to cart";
                btn.disabled = false;
            }
            alert("Failed to add to cart. Please try again.");
        }
    }

    /**
     * Reads the new cart count out of a re-rendered cart section, falling back
     * to the cart itself.
     *
     * The cart icon adds to the existing bubble only for updates coming from
     * the product form, and treats every other source as the absolute total.
     * Sending a hardcoded 1 from here reset the bubble to 1 regardless of what
     * was already in the cart.
     *
     * @param {Record<string, string>} sections - The re-rendered cart sections.
     * @param {string[]} sectionIds - The section ids that were requested.
     * @returns {Promise<number>} The number of items in the cart.
     */
    async #resolveCartItemCount(sections, sectionIds) {
        for (const sectionId of sectionIds) {
            const html = sections[sectionId];
            if (!html) continue;

            const node = new DOMParser()
                .parseFromString(html, "text/html")
                .querySelector('[ref="cartItemCount"]');

            if (node && node.textContent) return parseInt(node.textContent, 10);
        }

        try {
            const cart = await fetch("/cart.js").then((response) => response.json());
            return cart.item_count;
        } catch (_) {
            return 1;
        }
    }

    async addToWishlist() {
        if (!this.selectedVariant) return;

        // Use the WishlistStore from wishlist.js to add item
        if (window.WishlistStore) {
            window.WishlistStore.add({
                productHandle: this.currentProduct.handle,
                productId: this.currentProduct.id,
                variantId: this.selectedVariant.id,
            });
        } else {
            // Fallback: trigger event for existing wishlist system
            const event = new CustomEvent("wishlist:add", {
                detail: {
                    productId: this.currentProduct.id,
                    variantId: this.selectedVariant.id,
                    handle: this.currentProduct.handle,
                },
            });
            document.dispatchEvent(event);
        }

        // Show success feedback
        const btn = document.querySelector("[data-add-to-wishlist]");
        if (btn) {
            btn.textContent = "✓ Added to wishlist";
            btn.disabled = true;
        }

        setTimeout(() => {
            this.close();
        }, 800);
    }

    formatMoney(cents) {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "EUR",
        }).format(cents / 100);
    }

    async #fetchVariantShippingMap(handle) {
        const map = new Map();
        try {
            const res = await fetch(`/products/${handle}`);
            if (!res.ok) return map;
            const htmlText = await res.text();
            const doc = new DOMParser().parseFromString(htmlText, "text/html");
            doc.querySelectorAll('input[type="radio"][data-variant-id][data-shipping-speed]').forEach((el) => {
                const vid = el.getAttribute("data-variant-id");
                const speed = el.getAttribute("data-shipping-speed") || "standard";
                if (vid) map.set(String(vid), speed);
            });
            // Backward compat: also check old data-ship-48h attribute
            if (map.size === 0) {
                doc.querySelectorAll('input[type="radio"][data-variant-id][data-ship-48h]').forEach((el) => {
                    const vid = el.getAttribute("data-variant-id");
                    const fast = el.getAttribute("data-ship-48h") === "true";
                    if (vid) map.set(String(vid), fast ? "express" : "standard");
                });
            }
        } catch (_) {}
        return map;
    }

    #ensureStyles() {
        if (document.getElementById("qv-ship-styles")) return;
        const style = document.createElement("style");
        style.id = "qv-ship-styles";
        style.textContent = `
          /* Gallery */
          .qv-gallery { position: relative; overflow: hidden; background: white; }
          .qv-gallery-track { display: flex; width: 100%; transition: transform 280ms ease; touch-action: pan-y; }
          .qv-slide { flex: 0 0 100%; max-width: 100%; }
          .qv-slide img { display: block; width: 100%; height: auto; object-fit: cover; }
          .qv-badge { position: absolute; top: 8px; left: 8px; z-index: 20; display: inline-flex; align-items: center; gap: 4px; color: #000; padding: 4px 10px; font-size: 12px; font-weight: 700; border-radius: 999px; letter-spacing: .02em; line-height: 1; }
          .qv-badge svg { width: 16px; height: 16px; display: block; }
          .qv-badge__text { display: inline-block; }
          .qv-gallery-progress { position: absolute; left: 0; right: 0; bottom: 0px; height: 3px; background: rgba(0,0,0,0.08); border-radius: 2px; overflow: hidden; margin: 0px; }
          .qv-gallery-progress__bar { height: 100%; background: currentColor; color: #111; transition: transform 280ms ease; }
          .qv-gallery-nav { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(255,255,255,0.9); border: none; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s ease; z-index: 10; }
          .qv-gallery-nav:hover:not(:disabled) { background: rgba(255,255,255,1); box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
          .qv-gallery-nav:disabled { opacity: 0.3; cursor: not-allowed; }
          .qv-gallery-nav--prev { left: 12px; }
          .qv-gallery-nav--next { right: 12px; }
          .qv-gallery-nav svg { display: block; }
          @media (min-width: 769px) { 
            .quick-view-images { background: white; display: flex; align-items: center; justify-content: center; } 
            .qv-gallery-progress { display: none; }
          }
          @media (max-width: 768px) { .qv-gallery-nav { display: none; } }
          .qv-size-btn { position: relative; }
          .qv-ship-tag { position: absolute; top: 6px; right: 6px; display: inline-flex; width: 14px; height: 14px; opacity: .9; }
          .qv-ship-tag svg { width: 14px; height: 14px; display: block; }
          .qv-shipping-note { display: flex; align-items: center; gap: 8px; margin: 12px 0 0; color: #333; }
          .qv-shipping-note__icon { width: 18px; height: 18px; display: inline-flex; }
          .quick-view-variant-option__price { display: block; font-size: 12px; opacity: .8; }
          
          /* Size guide button styling - matches PDP */
          .quick-view-size-guide {
            background: transparent;
            border: none;
            color: inherit;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 13px;
            padding: 0;
            margin-left: auto;
          }
          .quick-view-size-guide:hover {
            text-decoration-thickness: 2px;
          }
          .quick-view-size-guide svg {
            width: 14px;
            height: 14px;
            display: block;
          }
          
          /* Size grid: 5 per row on desktop, 4 per row on mobile - matches PDP */
          .qv-size-grid {
            display: grid;
            gap: 1.25%;
          }
          @media (min-width: 750px) {
            .qv-size-grid {
              grid-template-columns: repeat(5, 19%) !important;
            }
          }
          @media (max-width: 749px) {
            .qv-size-grid {
              grid-template-columns: repeat(4, 23.5%) !important;
            }
          }
          
          /* Ensure size buttons fit properly in the grid */
          .qv-size-grid .quick-view-variant-option {
            width: 100%;
            min-width: 0;
          }
        `;
        document.head.appendChild(style);
    }

    #getProductImages(product) {
        const arr = Array.isArray(product.images) ? product.images : [];
        // product.images can be array of strings or objects with 'src'
        return arr.map((img) => (typeof img === "string" ? img : (img && img.src) || "")).filter(Boolean);
    }

    #normalizeUrl(url) {
        try {
            return String(url).split("?")[0];
        } catch {
            return String(url || "");
        }
    }

    #initGalleryHandlers() {
        const gallery = this.content.querySelector("[data-gallery]");
        if (!gallery) return;
        const track = gallery.querySelector(".qv-gallery-track");
        const bar = gallery.querySelector(".qv-gallery-progress__bar");
        const count = parseInt(gallery.getAttribute("data-count") || "1", 10) || 1;
        let startX = 0;
        let deltaX = 0;
        let dragging = false;

        const setIndex = (idx) => {
            const clamped = Math.max(0, Math.min(count - 1, idx));
            this.currentImageIndex = clamped;
            if (track) track.style.transform = `translateX(-${clamped * 100}%)`;
            if (bar) bar.style.transform = `translateX(${clamped * 100}%)`;
            gallery.setAttribute("data-index", String(clamped));

            // Update nav button states
            const prevBtn = gallery.querySelector("[data-gallery-prev]");
            const nextBtn = gallery.querySelector("[data-gallery-next]");
            if (prevBtn) prevBtn.disabled = clamped === 0;
            if (nextBtn) nextBtn.disabled = clamped === count - 1;
        };

        const onStart = (x) => {
            dragging = true;
            startX = x;
            deltaX = 0;
            track && (track.style.transition = "none");
            bar && (bar.style.transition = "none");
        };
        const onMove = (x) => {
            if (!dragging) return;
            deltaX = x - startX;
            const width = gallery.clientWidth || 1;
            const pct = (deltaX / width) * 100;
            const base = this.currentImageIndex * 100;
            track && (track.style.transform = `translateX(-${base - pct}%)`);
            bar && (bar.style.transform = `translateX(${base + pct}%)`);
        };
        const onEnd = () => {
            if (!dragging) return;
            dragging = false;
            const threshold = (gallery.clientWidth || 1) * 0.15; // 15% swipe
            if (deltaX > threshold) setIndex(this.currentImageIndex - 1);
            else if (deltaX < -threshold) setIndex(this.currentImageIndex + 1);
            else setIndex(this.currentImageIndex);
            // restore transitions
            requestAnimationFrame(() => {
                track && (track.style.transition = "transform 280ms ease");
                bar && (bar.style.transition = "transform 280ms ease");
            });
        };

        // Touch
        gallery.addEventListener("touchstart", (e) => onStart(e.touches[0].clientX), { passive: true });
        gallery.addEventListener("touchmove", (e) => onMove(e.touches[0].clientX), { passive: true });
        gallery.addEventListener("touchend", onEnd);
        // Mouse
        gallery.addEventListener("mousedown", (e) => onStart(e.clientX));
        window.addEventListener("mousemove", (e) => onMove(e.clientX));
        window.addEventListener("mouseup", onEnd);

        // Navigation buttons
        const prevBtn = gallery.querySelector("[data-gallery-prev]");
        const nextBtn = gallery.querySelector("[data-gallery-next]");
        if (prevBtn) {
            prevBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                setIndex(this.currentImageIndex - 1);
            });
        }
        if (nextBtn) {
            nextBtn.addEventListener("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                setIndex(this.currentImageIndex + 1);
            });
        }

        // Initialize button states
        setIndex(this.currentImageIndex);
    }
}

// Initialize when DOM is ready and store globally
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
        window.productQuickView = new ProductQuickView();
    });
} else {
    window.productQuickView = new ProductQuickView();
}

// Re-initialize after dynamic content loads
document.addEventListener("shopify:section:load", () => {
    if (window.productQuickView) {
        window.productQuickView.attachWishlistButtons();
    }
});
